import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type FailoverGroup, type FailoverMode, type FailoverState, type NodeInfo } from '../../api'
import { t, type Key } from '../../i18n'
import { Button, Chip, Note, Segmented, type State } from '../../ui'
import { NODES_KEY, fetchNodes } from '../Nodes'
import { FactorCancelled } from '../SecondFactor'
import { relayKeys, requestAsOf, type NodeInfoAsOf } from './api'
import { actionError, useConfirmedAction } from './confirm'
import { CreateGroupDialog } from './GroupCreate'
import { DEFAULT_GROUP_KEY, isDanger, useDefaultGroup, useDryRun } from './groupAdmin'
import { groupPath, isNodeGroup } from './groups'
import { tn } from './nodeSwitch'
import { WarningNotes } from './Warnings'

/*
 * Failover of a node (C0-DNS-1 v2, docs/architecture/ingest-cluster.md §11):
 * PUT /api/admin/nodes/{id}/failover {mode, group, backup} and the group's backup
 * (PATCH /api/admin/failover/groups/{id}). Every change goes with the second
 * factor (the code window opens on 403 totp_required, confirm.tsx).
 *
 * GROUP-UI-3 (NG-19, owner 01.11): on the design system (Segmented, .sx-select, Note, Chip),
 * EN / ES / RU. Modes None · Backup · Node group; "Cluster" (C1, deferred) is gone. The list
 * holds node groups only, with "Create node group" under it (the inline "new: dns label" made
 * C0 groups without a region, which the cabinet never sees). A node still in such an old group
 * gets a warn note and the way out. The dry run's warnings (V3: another site) come first.
 *
 * - Backup: a designated node takes this node's DNS name when it fails or is updated.
 * - Node group: the live members take the group's name by weight; the whole group down — the
 *   group's backup.
 */

export const FAILOVER_KEY = ['admin-failover'] as const

type Mode = 'none' | 'backup' | 'group'

const REASON: Record<string, Key> = {
  agent_silent: 'gx.fo.reason.agentSilent',
  agent_offline: 'gx.fo.reason.agentOffline',
  ingest_down: 'gx.fo.reason.ingestDown',
  logic_none: 'gx.fo.reason.logicNone',
  update: 'gx.fo.reason.update',
  drain: 'gx.fo.reason.drain',
  healthy: 'gx.fo.reason.healthy',
}

const LOOK: Record<'none' | 'backup' | 'group' | 'old', { state: State; word: Key }> = {
  none: { state: 'idle', word: 'gx.fo.chip.none' },
  backup: { state: 'idle', word: 'gx.fo.chip.backup' },
  group: { state: 'live', word: 'gx.fo.chip.group' },
  old: { state: 'warn', word: 'gx.fo.chip.old' },
}

const SELECT = 'sx-select w-full max-w-[360px] focus:border-accent focus:shadow-focus focus:outline-none max-md:min-h-[44px]'

export function useFailover(on = true) {
  return useQuery({ queryKey: FAILOVER_KEY, queryFn: () => api<FailoverState>('GET', '/api/admin/failover'), enabled: on, retry: false, refetchInterval: 15_000 })
}

export function NodeFailoverPanel({ n }: { n: NodeInfo }) {
  const qc = useQueryClient()
  const act = useConfirmedAction()
  const fo = useFailover()
  const dg = useDefaultGroup()
  const nodes = useQuery({ queryKey: NODES_KEY, queryFn: fetchNodes, refetchInterval: 15_000 })
  const cur = n.failover
  const curMode: FailoverMode = cur?.mode ?? 'none'
  const shown: Mode = curMode === 'cluster' ? 'group' : curMode
  const [mode, setMode] = useState<Mode>(shown)
  const [group, setGroup] = useState(cur?.group ?? '')
  const [backup, setBackup] = useState(cur?.backup ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState('')
  const [creating, setCreating] = useState(false)
  const createRef = useRef<HTMLElement | null>(null)
  const key = `${curMode}|${cur?.group ?? ''}|${cur?.backup ?? ''}`
  useEffect(() => {
    if (busy) return
    setMode(curMode === 'cluster' ? 'group' : curMode)
    setGroup(cur?.group ?? '')
    setBackup(cur?.backup ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const groups: FailoverGroup[] = useMemo(() => fo.data?.groups ?? [], [fo.data])
  const nodeGroups = groups.filter((g) => isNodeGroup(g))
  const candidates = (nodes.data ?? []).filter((x) => x.id !== n.id && x.status !== 'revoked' && !x.roles?.includes('integrations'))
  const nameOf = (id?: string) => (nodes.data ?? []).find((x) => x.id === id)?.name ?? id ?? ''
  const curGroup = groups.find((g) => g.id === (cur?.group ?? ''))
  const old = (curMode === 'group' && !!curGroup && !isNodeGroup(curGroup)) || curMode === 'cluster'
  // an old group stays chosen only while nothing is picked: it cannot be applied again
  const pickable = nodeGroups.some((g) => g.id === group)
  const changed = mode !== shown || old || (mode === 'group' && group !== (cur?.group ?? '')) || (mode === 'backup' && backup !== (cur?.backup ?? ''))
  const ready = mode === 'none' || (mode === 'backup' ? !!backup : pickable)
  const look = old ? LOOK.old : LOOK[shown]
  const dry = useDryRun(
    changed && ready && mode === 'group' ? { method: 'PUT', path: `/api/admin/nodes/${encodeURIComponent(n.id)}/failover`, body: { mode, group } } : null,
    dg.supported,
  )

  const send = async <T,>(what: string, f: (c: { totp?: string }) => Promise<T>): Promise<T | undefined> => {
    setBusy(true)
    setErr('')
    setDone('')
    try {
      const r = await act.run((c) => f(c))
      return r.ok ? r.data : undefined
    } catch (e) {
      if (!(e instanceof FactorCancelled)) setErr(actionError(e, what))
      return undefined
    } finally {
      setBusy(false)
    }
  }

  const apply = async () => {
    const body = { mode, ...(mode === 'backup' ? { backup } : {}), ...(mode === 'group' ? { group } : {}) }
    const r = await send(`PUT /api/admin/nodes/${n.id}/failover`, (c) =>
      requestAsOf<NodeInfoAsOf>(`/api/admin/nodes/${encodeURIComponent(n.id)}/failover`, { method: 'PUT', body: { ...body, ...(c.totp ? { totp: c.totp } : {}) } }),
    )
    if (!r) return
    qc.setQueryData(relayKeys.node(n.id), r)
    void qc.invalidateQueries({ queryKey: NODES_KEY })
    void qc.invalidateQueries({ queryKey: FAILOVER_KEY })
    void qc.invalidateQueries({ queryKey: DEFAULT_GROUP_KEY })
    setDone(t('gx.fo.saved'))
  }

  const setGroupBackup = async (g: FailoverGroup, b: string) => {
    const r = await send(`PATCH /api/admin/failover/groups/${g.id}`, (c) =>
      api<FailoverGroup>('PATCH', `/api/admin/failover/groups/${encodeURIComponent(g.id)}`, { backup: b || null, ...(c.totp ? { totp: c.totp } : {}) }),
    )
    if (r) {
      void qc.invalidateQueries({ queryKey: FAILOVER_KEY })
      setDone(b ? t('gx.fo.groupBackupSet', { group: g.name, name: nameOf(b) }) : t('gx.fo.groupBackupOff', { group: g.name }))
    }
  }

  const dnsOrder = async (action: 'failover' | 'restore') => {
    const r = await send(`POST /api/admin/nodes/${n.id}/dns-${action}`, (c) =>
      api<unknown>('POST', `/api/admin/nodes/${encodeURIComponent(n.id)}/dns-${action}`, { ...(action === 'failover' ? { reason: 'update' } : {}), ...(c.totp ? { totp: c.totp } : {}) }),
    )
    if (r !== undefined) {
      void qc.invalidateQueries({ queryKey: relayKeys.node(n.id) })
      void qc.invalidateQueries({ queryKey: NODES_KEY })
      setDone(t(action === 'failover' ? 'gx.fo.movedAway' : 'gx.fo.returned'))
    }
  }

  const locked = busy || n.status === 'revoked'
  const createLink = (
    <button type="button" className="text-accent hover:underline" data-testid="failover-create-group" onClick={(e) => ((createRef.current = e.currentTarget), setCreating(true))}>
      {t('gx.create.btn')}
    </button>
  )

  return (
    <section className="sx-panel sx-console flex flex-col gap-3" data-testid="node-failover">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="sx-label">{t('gx.fo.title')}</span>
        <Chip state={look.state} size="sm" data-testid="node-failover-mode" data-state={old ? 'old' : shown}>
          {t(look.word)}
        </Chip>
      </div>

      <p className="m-0 text-[13px] text-text-soft" data-testid="node-failover-now">
        {curMode === 'none' && t('gx.fo.now.none')}
        {curMode === 'backup' && tn('gx.fo.now.backup', { name: cur?.backup_name || cur?.backup || '—' })}
        {curMode === 'group' && !old && (
          <>
            {tn('gx.fo.now.group', { group: cur?.group_name ?? '—', fqdn: curGroup?.fqdn ?? '—' })}{' '}
            {curGroup?.backup ? tn('gx.fo.now.groupBackup', { name: nameOf(curGroup.backup) }) : t('gx.fo.now.noGroupBackup')}{' '}
            {curGroup && (
              <Link to={groupPath(curGroup.id)} className="text-accent hover:underline" data-testid="node-failover-group-link">
                {t('gx.fo.openGroup')}
              </Link>
            )}
          </>
        )}
      </p>

      {old && (
        <Note state="warn" data-testid="node-failover-old">
          <b className="font-medium text-text">{t(curMode === 'cluster' ? 'gx.fo.cluster' : 'gx.fo.old', { group: cur?.group_name ?? curGroup?.name ?? '—' })}</b>{' '}
          {t('gx.fo.oldB', { name: n.name })} {createLink}
        </Note>
      )}

      {cur?.dns && (
        <p className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]" data-testid="node-failover-dns" data-state={cur.dns.state}>
          <span className="text-text-soft">DNS:</span>
          <span className="text-text">
            {cur.dns.state === 'serving' && t('gx.fo.dns.serving')}
            {cur.dns.state === 'failed_over' && t('gx.fo.dns.failedOver', { to: (cur.dns.moved_to ?? []).map(nameOf).join(', ') || t('gx.fo.dns.noTarget') })}
            {cur.dns.state === 'recovering' && t('gx.fo.dns.recovering')}
          </span>
          {cur.dns.reason && <span className="text-muted">· {REASON[cur.dns.reason] ? t(REASON[cur.dns.reason]!) : cur.dns.reason}</span>}
          {cur.dns.order && <span className="text-muted">· {t('gx.fo.dns.order', { order: cur.dns.order })}</span>}
          {cur.dns.hold_until && <span className="text-muted">· {t('gx.fo.dns.hold', { at: new Date(cur.dns.hold_until).toISOString().slice(11, 19) })}</span>}
          {!cur.dns.heartbeat && <span className="text-warn">· {t('gx.fo.dns.oldAgent')}</span>}
        </p>
      )}

      {cur && !old && (
        <div className="flex flex-wrap gap-2">
          {cur.dns?.order ? (
            <Button size="sm" className="max-md:min-h-[44px]" data-testid="failover-dns-restore" disabled={locked} onClick={() => void dnsOrder('restore')}>
              {t('gx.fo.restore')}
            </Button>
          ) : (
            <Button size="sm" className="max-md:min-h-[44px]" data-testid="failover-dns-move" disabled={locked} onClick={() => void dnsOrder('failover')}>
              {t('gx.fo.moveAway')}
            </Button>
          )}
        </div>
      )}

      {cur?.warnings?.includes('egress_direct') && (
        <Note state="warn" data-testid="node-failover-egress-direct">
          {t('gx.fo.egressDirect')}
        </Note>
      )}

      <fieldset className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0" disabled={locked}>
        <div className="sx-field">
          <span className="lbl" id={`fo-mode-${n.id}`}>{t('gx.fo.mode')}</span>
          <Segmented
            className="self-start"
            label={t('gx.fo.mode')}
            value={mode}
            onChange={(v) => (setMode(v), setDone(''))}
            options={[
              { value: 'none', label: t('gx.fo.mode.none'), testId: 'failover-mode-none' },
              { value: 'backup', label: t('gx.fo.mode.backup'), testId: 'failover-mode-backup' },
              { value: 'group', label: t('gx.fo.mode.group'), testId: 'failover-mode-group' },
            ]}
          />
          <span className="help">{t(mode === 'none' ? 'gx.fo.hint.none' : mode === 'backup' ? 'gx.fo.hint.backup' : 'gx.fo.hint.group')}</span>
        </div>

        {mode === 'backup' && (
          <div className="sx-field">
            <label htmlFor={`fo-backup-${n.id}`}>{t('gx.fo.backupNode')}</label>
            <select id={`fo-backup-${n.id}`} data-testid="failover-backup" className={SELECT} value={backup} onChange={(e) => setBackup(e.target.value)}>
              <option value="">—</option>
              {candidates.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} · {x.region || t('gx.fo.noRegion')}
                </option>
              ))}
            </select>
          </div>
        )}

        {mode === 'group' && (
          <div className="sx-field">
            <label htmlFor={`fo-group-${n.id}`}>{t('gx.fo.group')}</label>
            <select id={`fo-group-${n.id}`} data-testid="failover-group" className={SELECT} value={pickable ? group : ''} onChange={(e) => setGroup(e.target.value)}>
              <option value="">{nodeGroups.length ? '—' : t('gx.fo.noGroups')}</option>
              {nodeGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} · {t(g.members.length === 1 ? 'gx.fo.members.one' : 'gx.fo.members.many', { n: g.members.length })}
                </option>
              ))}
            </select>
            <span className="help">
              {t('gx.fo.groupHelp')} {createLink}
            </span>
          </div>
        )}

        {mode === 'group' && pickable && (() => {
          const g = nodeGroups.find((x) => x.id === group)!
          return (
            <div className="sx-field" data-testid="failover-group-backup">
              <label htmlFor={`fo-gbackup-${n.id}`}>{t('gx.fo.groupBackup')}</label>
              <select id={`fo-gbackup-${n.id}`} className={SELECT} value={g.backup ?? ''} onChange={(e) => void setGroupBackup(g, e.target.value)}>
                <option value="">{t('gx.fo.none')}</option>
                {candidates
                  .filter((x) => !g.members.includes(x.id))
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name} · {x.region || t('gx.fo.noRegion')}
                    </option>
                  ))}
              </select>
              <span className="help">{t('gx.fo.groupBackupHelp')}</span>
            </div>
          )
        })()}
      </fieldset>

      {changed && ready && <WarningNotes warnings={dry.warnings} ctx={{ name: n.name, group: nodeGroups.find((g) => g.id === group)?.name ?? curGroup?.name }} testId="failover-warnings" />}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant={isDanger(dry.warnings) ? 'danger' : 'default'}
          data-testid="failover-apply"
          className="max-md:min-h-[44px]"
          disabled={locked || !changed || !ready || dry.pending}
          busy={busy}
          onClick={() => void apply()}
        >
          {busy ? t('gx.fo.applying') : t('gx.fo.apply')}
        </Button>
      </div>
      {fo.data?.provisioner?.error && (
        <Note state="warn" data-testid="failover-push-error">
          {t('gx.fo.provError', { why: fo.data.provisioner.error })}
        </Note>
      )}
      {err && (
        <Note state="fail" role="alert" data-testid="failover-error">
          {err}
        </Note>
      )}
      {done && (
        <Note role="status" data-testid="failover-done">
          {done}
        </Note>
      )}
      {creating && <CreateGroupDialog trigger={createRef} onClose={() => setCreating(false)} />}
    </section>
  )
}
